import { ComponentPropsWithoutRef } from "react";

import { VariantProps, cva } from "class-variance-authority";
import { IconType } from "react-icons";

import { cn } from "@/utils/cn";

const tabItemCSS = cva("text-base transition-colors duration-150", {
  variants: {
    variant: {
      enclosed:
        "w-40 rounded-t-lg border border-b-0 border-gray-light bg-gray-light/50 py-2.5 text-gray-dull",
      underlined:
        "grow border-b-2 border-gray-light py-3.5 text-base text-gray-dull",
      solid: "rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-gray-500 hover:border-primary/50 hover:text-primary",
    },
    active: {
      enclosed: "bg-white text-black font-medium",
      underlined: "border-primary text-black font-medium",
      solid: "border-primary bg-primary font-medium text-white hover:text-white",
    },
  },
});

export interface TabItemProps
  extends VariantProps<typeof tabItemCSS>,
    ComponentPropsWithoutRef<"button"> {
  __type: "Tab.Item";
  label?: string;
  icon?: IconType;
  isActive?: boolean;
}

const TabItem = ({
  variant,
  label = "",
  icon,
  isActive = false,
  className,
  ...props
}: TabItemProps) => {
  const Icon = icon;
  const { disabled: isDisabled } = props;

  return (
    <button
      type="button"
      className={cn(
        tabItemCSS({ variant }),
        isActive && tabItemCSS({ active: variant }),
        isDisabled && "text-gray-middle",
        variant === "solid" && !Icon && "relative w-24 break-keep py-1.5",
        className,
      )}
      {...props}
    >
      {Icon ? (
        <Icon
          className={cn("size-6 text-gray-dark", isActive && "text-white")}
        />
      ) : (
        label
      )}
    </button>
  );
};

TabItem.defaultProps = {
  __type: "Tab.Item",
};

export default TabItem;
